import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/store';
import { Stepper } from 'primereact/stepper';
import { StepperPanel } from 'primereact/stepperpanel';
import { Button } from 'primereact/button';
import api from '../services/api';
import { Flex } from '@radix-ui/themes';
import { Information } from './setup/Information';
import CreateNonFictionChapters from '../components/create/CreateNonFictionChapters';
import CreateNonFictionTopics from '../components/create/CreateNonFictionTopics';
import { v4 as uuidv4 } from 'uuid';

export const CreateNonFiction = () => {
    const [state, actions] = useStore();
    const stepperRef = useRef(null);
    const navigate = useNavigate();

    // State variables for form data
    const [title, setTitle] = useState('');
    const [topic, setTopic] = useState('');
    const [styleAndTone, setStyleAndTone] = useState('');
    const [otherInstructions, setOtherInstructions] = useState('');
    const [novelId, setNovelId] = useState(null);
    
    // State for chapters and topics - using objects with IDs instead of arrays
    const [selectedChapters, setSelectedChapters] = useState([]);
    const [selectedTopics, setSelectedTopics] = useState({});

    // Move generatedSets state from child components to here
    const [chapterGeneratedSets, setChapterGeneratedSets] = useState([]);
    const [chapterCurrentSetIndex, setChapterCurrentSetIndex] = useState(-1);
    const [topicGeneratedSets, setTopicGeneratedSets] = useState({});
    const [topicCurrentSetIndex, setTopicCurrentSetIndex] = useState({});

    useEffect(() => {
        if (state.readOnly) {
            navigate('/dashboard');
        }
    }, []);

    // Handler for generating chapters (moved from CreateNonFictionChapters)
    const handleGenerateChapters = async () => {
        if (!topic?.trim()) {
            return { error: "Please enter a topic" };
        }

        try {
            const response = await api.generateNonFictionChapters(novelId, topic, styleAndTone, otherInstructions);
            
            if (response.success) {
                setChapterGeneratedSets([...chapterGeneratedSets, response.chapters]);
                setChapterCurrentSetIndex(chapterGeneratedSets.length);
                return { success: true };
            } else {
                return { error: response.error || "Failed to generate chapters" };
            }
        } catch (err) {
            console.error("Error generating non-fiction chapters:", err);
            return { error: "An error occurred while generating chapters" };
        }
    };

    // Handler for generating topics (moved from CreateNonFictionTopics)
    const handleGenerateTopics = async (chapterTitle, chapterId) => {
        if (!chapterTitle?.trim()) {
            return { error: "No chapter selected" };
        }

        try {
            const response = await api.generateNonFictionTopics(novelId, chapterTitle);
            
            if (response.success) {
                setTopicGeneratedSets(prev => {
                    const updatedSets = { ...prev };
                    if (!updatedSets[chapterId]) {
                        updatedSets[chapterId] = [];
                    }
                    updatedSets[chapterId].push(response.topics);
                    return updatedSets;
                });
                
                setTopicCurrentSetIndex(prev => ({
                    ...prev,
                    [chapterId]: prev[chapterId] !== undefined ? 
                        prev[chapterId] + 1 : 0
                }));
                
                return { success: true };
            } else {
                return { error: response.error || "Failed to generate topics" };
            }
        } catch (err) {
            console.error("Error generating non-fiction topics:", err);
            return { error: "An error occurred while generating topics" };
        }
    };

    const handleChapterSetNavigation = (direction) => {
        const newIndex = chapterCurrentSetIndex + direction;
        if (newIndex >= 0 && newIndex < chapterGeneratedSets.length) {
            setChapterCurrentSetIndex(newIndex);
        }
    };

    const handleTopicSetNavigation = (chapterId, direction) => {
        const sets = topicGeneratedSets[chapterId] || [];
        const currentIndex = topicCurrentSetIndex[chapterId] || 0;
        const newIndex = currentIndex + direction;
        
        if (newIndex >= 0 && newIndex < sets.length) {
            setTopicCurrentSetIndex({
                ...topicCurrentSetIndex,
                [chapterId]: newIndex
            });
        }
    };

    const handleFinish = async () => {
        try {
            // Save chapters with their associated topics
            if (selectedChapters.length > 0) {
                // Map the chapter objects with their topics for the API
                const chaptersWithTopics = selectedChapters.map(chapter => ({
                    title: chapter.title,
                    topics: selectedTopics[chapter.id] || []
                }));
                
                await api.saveNonFictionChapters(novelId, chaptersWithTopics);
            }
            
            // Navigate to the novel page
            navigate(`/novel/${novelId}`);
        } catch (error) {
            console.error("Error saving non-fiction book:", error);
        }
    };

    return (
        <div style={{ margin: '0 auto', width: '75%' }}>
            <div className="card flex justify-content-center">
                <Stepper linear ref={stepperRef} style={{ flexBasis: '50rem' }}>
                    <StepperPanel header="Information">
                        <div className="flex flex-column h-12rem">
                            <Information 
                                title={title}
                                setTitle={setTitle}
                                topic={topic} 
                                setTopic={setTopic} 
                                styleAndTone={styleAndTone} 
                                setStyleAndTone={setStyleAndTone} 
                                otherInstructions={otherInstructions} 
                                setOtherInstructions={setOtherInstructions}
                            />
                        </div>
                        <br />
                        <div className="flex pt-4 justify-content-end">
                            <Button 
                                label="Next" 
                                icon="pi pi-arrow-right" 
                                iconPos="right" 
                                className="custom-button"
                                onClick={async () => {
                                    // Create the non-fiction novel before proceeding
                                    const novel = await api.createNovel({
                                        title: title || 'Untitled Non-Fiction',
                                        type: 'non-fiction',
                                        logline: topic,
                                        writingPreferences: {
                                            tone: styleAndTone,
                                            other: otherInstructions,
                                        }
                                    });
                                    setNovelId(novel.novelId);
                                    stepperRef.current.nextCallback();
                                }}
                            />
                        </div>
                    </StepperPanel>
                    <StepperPanel header="Chapters">
                        <div className="flex flex-column h-12rem">
                            <CreateNonFictionChapters 
                                novelId={novelId} 
                                topic={topic}
                                style={styleAndTone}
                                otherInstructions={otherInstructions}
                                selectedChapters={selectedChapters}
                                setSelectedChapters={setSelectedChapters}
                                generatedSets={chapterGeneratedSets}
                                currentSetIndex={chapterCurrentSetIndex}
                                onGenerateChapters={handleGenerateChapters}
                                onNavigateSet={handleChapterSetNavigation}
                            />
                        </div>
                        <div className="flex pt-4 justify-content-end" style={{ position: 'relative' }}>
                            <Button 
                                style={{ position: 'absolute', right: '10px', bottom: '-5px', width: 'fit-content' }}
                                label="Next" 
                                className="custom-button"
                                disabled={selectedChapters.length === 0}
                                onClick={() => stepperRef.current.nextCallback()}
                            />
                        </div>
                    </StepperPanel>
                    <StepperPanel header="Topics">
                        <div className="flex flex-column h-12rem">
                            <CreateNonFictionTopics
                                novelId={novelId}
                                selectedChapters={selectedChapters}
                                selectedTopics={selectedTopics}
                                setSelectedTopics={setSelectedTopics}
                                generatedSets={topicGeneratedSets}
                                currentSetIndex={topicCurrentSetIndex}
                                onGenerateTopics={handleGenerateTopics}
                                onNavigateSet={handleTopicSetNavigation}
                            />
                        </div>
                        <Flex justify={'between'}>
                            <Button 
                                label="Back" 
                                className="custom-button"
                                onClick={() => stepperRef.current.prevCallback()}
                            />
                            <Button 
                                label="Finish" 
                                className="custom-button"
                                onClick={handleFinish}
                            />
                        </Flex>
                    </StepperPanel>
                </Stepper>
            </div>
        </div>
    );
}

export default CreateNonFiction;